// File: src/components/QRScannerModal.jsx
import { useEffect, useRef } from "react";
import {
  Html5QrcodeScanner,
  Html5QrcodeScanType,
} from "html5-qrcode";

export default function QRScannerModal({ onScan, onClose }) {
  const boxId  = "qr-scanner-box";
  const scannerRef = useRef(null);
  const closing = useRef(false);

  // Safe clear helper (avoid double-clears throwing)
  const safeClear = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.clear(); } catch {}
      scannerRef.current = null;
    }
  };

  useEffect(() => {
    // Dynamically size the scan box (~60% of the shortest side)
    const qrbox = (vw, vh) => {
      const size = Math.round(Math.min(vw, vh) * 0.6);
      return { width: size, height: size };
    };

    try {
      scannerRef.current = new Html5QrcodeScanner(
        boxId,
        {
          fps: 5, // throttle for iPad stability
          qrbox,
          rememberLastUsedCamera: true,
          showTorchButtonIfSupported: true,
          showZoomSliderIfSupported: true,
          supportedScanTypes: [
            Html5QrcodeScanType.SCAN_TYPE_CAMERA,
            Html5QrcodeScanType.SCAN_TYPE_FILE, // allow "Scan an image" tab
          ],
        },
        /* verbose */ false
      );

      scannerRef.current.render(
        async (decodedText /* string */) => {
          if (closing.current) return;
          closing.current = true;
          await safeClear();
          onScan(decodedText.trim());
          onClose();
        },
        // Per-frame errors are noisy; silence them
        () => {}
      );
    } catch (e) {
      // If init fails on this device, just close the modal gracefully
      // (You could also set a local fallback UI if you like)
      onClose();
    }

    return () => { safeClear(); };
  }, [onScan, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      <button
        onClick={() => { closing.current = true; safeClear().finally(onClose); }}
        className="absolute top-4 right-4 text-white text-2xl"
        aria-label="Close"
      >
        ×
      </button>

      <div id={boxId} className="w-[320px] h-[320px] bg-black rounded-md" />

      <p className="absolute bottom-8 text-sm text-gray-300 text-center px-4">
        If the camera isn’t available, tap <em>Scan an image</em> and use <em>Take Photo</em> or choose from your library.
      </p>
    </div>
  );
}
